/**
 * Which One.com WSB plan a merchant is on, as far as appointments are concerned.
 *
 * The three plans differ only by their monthly appointment allowance, and the
 * subscription response is where that allowance reaches app-shell — see
 * `SubscriptionLimits` in `@shore/types`, written by subscription_service's
 * `serialize_limits/1`:
 *
 *   | plan      | monthly_appointments_creation_limit | appointments        |
 *   |-----------|-------------------------------------|---------------------|
 *   | Portfolio | `0`                                 | none at all         |
 *   | Bookings  | a positive number, typically `25`   | capped monthly      |
 *   | Commerce  | key absent                          | unlimited           |
 *
 * Read from the subscription rather than from `/usage`, because the two capped
 * plans need to be told apart *before* any usage exists: Portfolio has no
 * allowance to spend, so nothing is ever fetched for it. It is also the only
 * signal that survives the backend's own fail-open rule — `/usage` answers
 * `booking_allowed: true` at a limit of zero (`AppointmentsUsage` in
 * subscription_service, mirroring Core's `MonthlyAppointmentLimit#can_create?`),
 * which is the opposite of what Portfolio means to the merchant.
 */
import { WSB_MERCHANT_SOURCE } from '../../shared/constants/merchant';

export type WsbPlan = 'portfolio' | 'bookings' | 'commerce';

/** The plan a merchant on a capped plan is upsold to. */
export type UpgradeTarget = 'bookings' | 'commerce';

/**
 * Resolves the merchant's plan, or `null` when they are not on WSB at all.
 *
 * @param source - `currentMerchant.source`.
 * @param monthlyAppointmentLimit - `subscription.limits
 *   .monthly_appointments_creation_limit`, absent for merchants whose plan sets
 *   no cap.
 */
export function resolveWsbPlan(
  source: string,
  monthlyAppointmentLimit: number | null | undefined
): WsbPlan | null {
  if (source !== WSB_MERCHANT_SOURCE) return null;

  if (typeof monthlyAppointmentLimit !== 'number') return 'commerce';

  return monthlyAppointmentLimit > 0 ? 'bookings' : 'portfolio';
}

/**
 * The plan we sell to a merchant on `plan`, or `null` when there is nothing
 * above them.
 *
 * Each capped plan is upsold one step up, and the step decides the copy: a
 * Portfolio merchant is offered appointments ("Ready for bookings?"), a Bookings
 * merchant is offered the shop ("Ready to sell?"). It also decides which price
 * the One.com parent window is asked to quote — see `PRICING_REQUEST_MESSAGE_TYPE`.
 */
export function upgradeTargetFor(plan: WsbPlan): UpgradeTarget | null {
  switch (plan) {
    case 'portfolio':
      return 'bookings';
    case 'bookings':
      return 'commerce';
    default:
      return null;
  }
}
